import { cloneElement, isValidElement, useMemo, useState } from "react";

import {
  ChevronRightOutlined,
  DownloadOutlined,
  DrawRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Paper,
  Typography,
} from "@mui/material";
import { grey, orange } from "@mui/material/colors";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import { format } from "date-fns";
import { isArray, isEmpty, isNil, merge, size } from "lodash";
import { useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import AnimatedCheckmark from "../../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import { RffSectionAuthorizationSignature } from "../../components/finalForm/common/CreditAuthorizationFormQuestions";
import RffForm from "../../components/finalForm/RffForm";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeader from "../../components/layout/TitledHeader";
import BasicModal from "../../components/modals/BasicModal";
import PdfDownloadButton from "../../components/pdf/components/PdfDownloadButton";
import FinancingRequestPDF from "../../components/pdf/FinancingRequestPDF";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import { useEndpointHook } from "../../hooks/useEndpointHook";
import { profileSet } from "../../redux/actions/profileActions";
import { financingRequestAddToEnd } from "../../redux/reducers/financingRequestsReducer";
import BorrowersSection from "./BorrowersSection";
import BorrowingEntitiesSection from "./BorrowingEntitiesSection";
import FileUploadSection from "./FileUploadSection";
import LoanDetailSection from "./LoanDetailSection";
import PropertiesSection from "./PropertiesSection";
import TitledLoanSection from "./TitledLoanSection";

const LOAN_CONTACT_ROLES = [
  "Appraisal Point of Contact",
  "Title Agent",
  "Insurance Agent",
];

const NewFinancingRequestScreen = () => {
  const [success, setSuccess] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);

  const [formData, setFormData] = useState(null);
  const {
    loading,
    createNewFinancingRequest,
    getBorrowerFinancingApplications,
    getProfile,
  } = useEndpointHook();

  const navigate = useNavigate();

  const handleDownloadPdf = () => {
    if (!pdfBlob) return;
    const downloadUrl = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `Loan_Application_${format(new Date(), "MM-dd-yyyy")}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleReturnHome = () => {
    navigate("/");
  };

  // ----------------
  const onFormSubmit = async (submitData) => {
    let data = { ...submitData };
    let loanName = submitData?.loanName;

    const filteredContacts = (data.loanRelatedContacts || [])
      .map((contact, i) => ({
        ...contact,
        loanRole: LOAN_CONTACT_ROLES[i],
      }))
      .filter((contact) => {
        const { loanRole, ...fields } = contact;
        return Object.values(fields).some((val) => !isNil(val) && val !== "");
      });

    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = data?.subjectProperties[0]?.address?.fullAddress;
      if (propertyName) {
        loanName = propertyName;
      }
    }
    data.loanName = loanName;

    const blob = await pdf(<FinancingRequestPDF formData={data} />).toBlob();
    const file = new File([blob], "loan-application.pdf", {
      type: "application/pdf",
    });
    const pdfFileObj = {
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: `Loan Application - ${format(new Date(), "MM-dd-yyyy")}.pdf`,
      docGroup: "Borrower Documents",
      docType: "Loan Application",
    };

    const existingFiles = data.uploadMetadata || [];
    data.uploadMetadata = [...existingFiles, pdfFileObj];

    createNewFinancingRequest({
      data: {
        ...data,
        loanRelatedContacts: filteredContacts,
      },
      onSuccessFn: (res) => {
        console.log("New Financing Request Created:", res);
        const updatedProfile = res?.user;
        const newLoanData = res?.created_loan;

        if (isEmpty(updatedProfile)) {
          getProfile({});
        } else {
          dispatch(profileSet(updatedProfile));
        }

        if (isEmpty(newLoanData)) {
          getBorrowerFinancingApplications({});
        } else {
          dispatch(financingRequestAddToEnd(newLoanData));
        }

        setPdfBlob(blob);
        setSuccess(true);
      },
    });
  };

  const defaultValues = useMemo(() => {
    return {
      borrowers: [{}],
      subjectProperties: [{}],
    };
  }, []);

  const initialState = useMemo(() => {
    if (!isEmpty(formData)) {
      return formData;
    }

    const initialBorrowerData = {
      dateOfBirth: profile.dateOfBirth,
      emailAddress: profile.emailAddress,
      firstName: profile.firstName,
      lastName: profile.lastName,
      maritalStatus: profile.maritalStatus,
      phone: profile.phone,
      borrowerHasSSN: isNil(profile.borrowerSSN) ? false : true,
      citizenshipStatus: profile.citizenshipStatus,
      primaryAddress: profile?.primaryAddress,
      borrowerSSN: profile?.borrowerSSN,
    };

    const initialBorrower = {
      borrowers: [initialBorrowerData],
    };

    let combinedValues = merge(defaultValues, initialBorrower);
    return combinedValues;
  }, [formData, profile, defaultValues]);

  // ── Success Screen ──
  if (success) {
    return (
      <Screen
        sx={{
          position: "absolute",
          left: 0,
          width: "100%",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f8fa",
        }}
      >
        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            p: 0,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Flx
            center
            column
            sx={{
              borderBottom: `1px solid ${grey[300]}`,
              py: 4,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <AnimatedCheckmark />
            </Box>
            <Htag sx={{ color: grey[900], fontWeight: 700, mb: 0.5 }}>
              Financing Request Submitted
            </Htag>
            <Typography sx={{ color: grey[700], fontSize: 14 }}>
              Your application is now being reviewed
            </Typography>
          </Flx>

          {/* Body */}
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 14,
                mb: 1.5,
                lineHeight: 1.6,
              }}
            >
              A PDF copy of your loan application is available below for your
              records.
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 14,
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              To help expedite your review, you can upload supporting documents
              like tax returns, bank statements, or property files directly from
              your loan details page.
            </Typography>

            <Flx column gap={1.5}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<DownloadOutlined />}
                onClick={handleDownloadPdf}
                disabled={!pdfBlob}
              >
                Download Loan Application PDF
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleReturnHome}
              >
                Go to My Loans
              </Button>
            </Flx>
          </Box>
        </Card>
      </Screen>
    );
  }

  // ── Form Screen ──
  return (
    <Screen
      sx={{
        position: "absolute",
        left: 0,
        width: "100%",
        height: "100vh",
      }}
    >
      <TitledHeader
        title={"New Financing Request"}
        description="Fill out the details below to submit a new financing request to your loan team."
      />
      <RffForm
        initialValues={initialState}
        onSubmit={onFormSubmit}
        success={success}
        // formSpy
        loading={loading}
        submitText="Submit Financing Request"
        maxWidth={"md"}
        sx={{
          pb: 6,
          px: 1.5,
          background: "#f5f8fa",
          ".titled-group-header-root": {
            "h1, svg": {
              fontSize: "18px !important",
            },
          },
          h4: {
            fontWeight: 500,
            fontSize: "14px !important",
          },
        }}
      >
        <Flx column gap={4} sx={{ py: 4 }}>
          {/* <PdfPreviewSection>
            <FinancingRequestPDF />
          </PdfPreviewSection> */}
          {/* <StagedApplicationsContent setFormData={setFormData} /> */}
          <FinancingRequestFormQuestions />
        </Flx>
      </RffForm>
    </Screen>
  );
};

export const FinancingRequestFormQuestions = ({ children }) => {
  return (
    <>
      <LoanDetailSection />
      <PropertiesSection />
      <BorrowersSection />
      <BorrowingEntitiesSection />
      <FileUploadSection />
      <TitledLoanSection
        variant="h2"
        icon={<DrawRounded className="thin" />}
        title="Signature & Authorization"
      >
        <RffSectionAuthorizationSignature hideTitle />
      </TitledLoanSection>
    </>
  );
};

/**
 * Drop this component inside your RffForm (it needs form context).
 * It reads current form values and renders a live PDF preview + download button.
 */
const PdfPreviewSection = ({ children }) => {
  const [showPreview, setShowPreview] = useState(false);
  const { values } = useFormState({ subscription: { values: true } });

  // Clone the child PDF component with formData and button props injected
  const renderPdf = (extraProps = {}) => {
    if (!isValidElement(children)) return null;
    return cloneElement(children, { formData: values, ...extraProps });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Flx gap={1.5} ac>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            showPreview ? <VisibilityOffRounded /> : <VisibilityRounded />
          }
          onClick={() => setShowPreview((prev) => !prev)}
        >
          {showPreview ? "Hide PDF Preview" : "Preview PDF"}
        </Button>
        <PdfDownloadButton
          filename={`Loan_Application_${formatDate(new Date())}.pdf`}
        >
          {renderPdf()}
        </PdfDownloadButton>
        {/* Download button — injects button=true */}
        {/* {renderPdf({ button: true })} */}
      </Flx>

      <Collapse in={showPreview}>
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            overflow: "hidden",
            height: 600,
          }}
        >
          <PDFViewer
            width="100%"
            height="100%"
            showToolbar
            style={{ border: "none" }}
          >
            {renderPdf()}
          </PDFViewer>
        </Paper>
      </Collapse>
    </Box>
  );
};

const StagedApplicationsContent = ({ setFormData }) => {
  const profile = useSelector((state) => state.profile);
  const [show, setShow] = useState(false);
  const stagedApplications = profile?.stagedApplications;

  const handleSelectApplication = (app) => {
    setFormData(app);
    setShow(false);
  };
  return (
    <>
      {!isEmpty(stagedApplications) && (
        <Alert
          sx={{ border: `1px solid ${orange[200]}` }}
          severity="warning"
          action={<Button onClick={() => setShow(true)}>View</Button>}
        >
          You have <b>{size(stagedApplications)}</b> staged applications created
          for you. Would you like to apply any of that information to this
          application?
        </Alert>
      )}

      <BasicModal
        title={"Staged Applications"}
        show={show}
        maxWidth="sm"
        onClose={() => setShow(false)}
      >
        <Flx column gap={1}>
          <Txt>Select from the following staged applications:</Txt>
          <Flx column gap={2}>
            {stagedApplications?.map((app) => {
              return (
                <Card
                  key={app.id}
                  // onClick={() => handleSelectApplication(app)}
                  sx={{ border: `1px solid ${grey[200]}`, p: 0 }}
                >
                  <Flx column g={0}>
                    <Flx
                      fw
                      jb
                      ac
                      g={1}
                      wrap
                      sx={{
                        //
                        // background: blueGrey[900],
                        background: grey[100],
                        p: 2,
                      }}
                    >
                      <Txt
                        fontSize={15}
                        bold
                        sx={
                          {
                            //
                            // color: "#ffffff",
                          }
                        }
                      >
                        {app.loanName ||
                          app.subjectProperties?.[0]?.address?.fullAddress ||
                          "Untitled Application"}
                      </Txt>
                      <Flx end sx={{ flexGrow: 1 }}>
                        <Button
                          size="medium"
                          endIcon={<ChevronRightOutlined />}
                          onClick={() => handleSelectApplication(app)}
                          key={app.id}
                        >
                          Select
                        </Button>
                      </Flx>
                    </Flx>
                    <Flx column sx={{ p: 1 }}>
                      <DetailRowList
                        items={[
                          {
                            label: "Loan Name",
                            value: app?.loanName,
                          },
                          {
                            label: "Loan Product Type",
                            value: app?.loanProductType,
                          },
                          {
                            label: "Loan Purpose",
                            value: app?.loanPurpose,
                          },
                          {
                            label: "Created Date",
                            value: formatDate(app?.stagedDate),
                          },
                          {
                            label: "Created By",
                            value: app?.createdBy,
                          },
                        ]}
                      />
                    </Flx>
                  </Flx>
                </Card>
              );
            })}
          </Flx>
        </Flx>
      </BasicModal>
    </>
  );
};

const DetailRow = ({ label, value, valueSx, fontSize = "11px", onClick }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
        py: 1,
        px: 1.5,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick
          ? { bgcolor: "action.hover", mx: -1.5, px: 1.5, borderRadius: 1 }
          : {},
      }}
    >
      <Typography
        sx={{
          fontSize: fontSize,
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: fontSize,
          fontWeight: 500,
          color: "text.primary",
          textAlign: "right",
          wordBreak: "break-word",
          ...valueSx,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
};

// ─── Stacked list of detail rows with dividers ─────────────
const DetailRowList = ({ items = [], divider = true }) => {
  return (
    <Box
      sx={
        {
          // border: "1px solid",
          // borderColor: "grey.200",
          // bgcolor: "#fff",
          // borderRadius: "10px",
          // p: "12px 14px",
        }
      }
    >
      {items.map((item, i) => (
        <Box
          key={item.label || i}
          sx={{
            borderBottom:
              divider && i < items.length - 1 ? "1px solid" : "none",
            borderColor: "grey.200",
          }}
        >
          <DetailRow {...item} />
        </Box>
      ))}
    </Box>
  );
};

const e = {
  subjectProperties: [
    {
      address: {
        id: "A-1yb5xFhzjCuSufdQxVXQ-1774637851442",
        fullAddress: "125 Carley Avenue, Huntington, NY 11743",
        streetNumber: "125",
        streetName: "Carley Avenue",
        zip: "11743",
        county: "Suffolk",
        state: "NY",
        city: "Huntington",
        country: "United States",
        countryCode: "US",
        latitude: 40.866112,
        longitude: -73.43475,
        coordinateTuple: [40.866112, -73.43475],
        score: 1,
      },
      propertyType: "SFR",
    },
  ],
  borrowers: [
    {
      emailAddress: "olivajames110@gmail.com",
      firstName: "James",
      lastName: "Oliva",
      phone: "6314563373",
      ff_sold: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
      ff_retained_as_rental: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
      income_producing_properties: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
      guc_sold: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
      guc_retained_as_rental: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
      general_contractor_projects: {
        less_than_three_years: 0,
        greater_than_three_years: 0,
      },
    },
  ],
  loanProductType: "Fix and Flip",
  loanPurpose: "Purchase",
  baseLoanAmount: 450000,
  loanName: "125 Carley Avenue, Huntington, NY 11743",
  _id: "69c6d34838a69504b564e46f",
  createdDate: "2026-03-27T18:58:16.522000",
  createdBy: "dev2@arrowservicing.com",
  stagedDate: "2026-03-27T18:58:16.522000",
  applicationStatus: "staged",
  startedByAdmin: true,
  startedByUser: false,
};

export default NewFinancingRequestScreen;
